import * as React from 'react';

import { NETWORK_ERROR_FOR_DISPLAY, NO_CONNECTION_MESSAGE, PLEASE_CHECK_CONNECTION_MESSAGE } from 'consts';
import { useErrorScreenContext } from 'context/error/error';
import { useHandleFullScreenError } from 'hooks/App/useAppErrorReaction/handlers/useHandleFullScreenError';
import networkService from 'services/networkService';

import extractImportUrl from './helpers/extractImportUrl';

/**
 * `LazyLoader` is a function for handling a failed router boot and displaying an error screen in a web application
 *
 * @param promise - An promise().
 * @param Loader - An fallback component for suspense.
 *
 * @returns An internal function that returns the suspense component of react js with
 *
 */

const ID = 1;
let fallBackUrl = ''; // To store a fallback url

function LazyLoader<T = unknown>(
  promise: () => Promise<{ default: React.ComponentType<any> }>,
  Loader?: (props: any) => JSX.Element | any,
) {
  let ResultForErrorCase: Promise<{ default: React.ComponentType<any> }>  = import(
    './components/DefaultComponent'
  ).then((module) => ({ default: module.default }));

  function LazyLoaderInternal(props: T) {
    const handleFullScreenError = useHandleFullScreenError();
    const errorScreenContext = useErrorScreenContext();

    const retryImport = async () => {
      try {
        return await promise();
      } catch (error) {
        const isNoConnection = !(await networkService.isNetworkConnection());
        // for avoiding repeated downloads of files
        if (!fallBackUrl) {
          // this assumes that the exception will contain this specific text with the url of the module
          // if not, the url will not be able to parse and we'll get an error on that
          // eg. "Failed to fetch dynamically imported module: https://example.com/assets/Home.tsx"
          const extractedImportUrl = error && (extractImportUrl(error as Error));

          if (extractedImportUrl) {
            const url = extractedImportUrl as any;
            // add a timestamp to the url to force a reload the module (and not use the cached version - cache busting)
            url.searchParams.set('t', `${+new Date()}`);
            fallBackUrl = url.href;
          }
        }

        if (isNoConnection) {
          const onceAgain = () => async () => {
            errorScreenContext.clearError();
            return true;
          };

          const commonError = {
            code: NETWORK_ERROR_FOR_DISPLAY,
            title: NO_CONNECTION_MESSAGE,
            message: PLEASE_CHECK_CONNECTION_MESSAGE,
            onRetry: onceAgain(),
          };

          handleFullScreenError(commonError, ID, false);
        } else {
          try {
            ResultForErrorCase = await import(/* @vite-ignore */ fallBackUrl);
          } catch (e) {
            console.warn('retrying import');
          }
        }

        return ResultForErrorCase;
      }
    };

    const Component = React.lazy(retryImport);

    return (
      <React.Suspense fallback={Loader ? <Loader /> : null}>
        <Component {...props} />
      </React.Suspense>
    );
  }

  return LazyLoaderInternal;
}

export default LazyLoader;
